import { useMemo, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Id } from "../../../../convex/_generated/dataModel";
import { CaptionStyles } from "../types";
import { CaptionStylePreset } from "../types/caption-styles";
import { BUILT_IN_PRESETS } from "../templates/caption-templates";

/**
 * Hook for managing caption style presets
 *
 * Combines built-in presets with user-saved custom presets stored in Convex.
 * Provides functions to save and delete user presets.
 */
export function useCaptionPresets(projectId: Id<"projects"> | null) {
  const project = useQuery(
    api.projects.getProject,
    projectId ? { projectId } : "skip"
  );

  const saveCaptionSettings = useMutation(api.projects.saveCaptionSettings);

  // Parse user presets from captionSettings.customFontPresets
  const userPresets = useMemo((): CaptionStylePreset[] => {
    if (!project?.captionSettings?.customFontPresets) {
      return [];
    }

    try {
      const parsed = JSON.parse(project.captionSettings.customFontPresets);
      // Filter to only caption style presets (not the old font presets)
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (preset: any) =>
            preset.id &&
            preset.name &&
            preset.styles &&
            !preset.isBuiltIn
        );
      }
      return [];
    } catch {
      return [];
    }
  }, [project?.captionSettings?.customFontPresets]);

  // Combine built-in and user presets
  const allPresets = useMemo((): CaptionStylePreset[] => {
    return [...BUILT_IN_PRESETS, ...userPresets];
  }, [userPresets]);

  // Save a new user preset
  const savePreset = useCallback(
    async (name: string, styles: CaptionStyles): Promise<void> => {
      if (!projectId || !project) {
        throw new Error("Project not found");
      }

      const newPreset: CaptionStylePreset = {
        id: `user-${Date.now()}`,
        name,
        isBuiltIn: false,
        createdAt: Date.now(),
        styles,
      };

      const updatedPresets = [...userPresets, newPreset];

      // Get existing caption settings or create default
      const existingSettings = project.captionSettings || {
        customColors: [],
        activeFontPresetId: "classic",
        customFontPresets: "[]",
        captionPosition: 85,
        fontSize: 24,
        textTransform: "none",
        captionAnimation: "none",
      };

      await saveCaptionSettings({
        projectId,
        captionSettings: {
          ...existingSettings,
          customFontPresets: JSON.stringify(updatedPresets),
        },
      });
    },
    [projectId, project, userPresets, saveCaptionSettings]
  );

  // Delete a user preset
  const deletePreset = useCallback(
    async (presetId: string): Promise<void> => {
      if (!projectId || !project) {
        throw new Error("Project not found");
      }

      // Don't allow deleting built-in presets
      if (presetId.startsWith("classic") || BUILT_IN_PRESETS.some((p) => p.id === presetId)) {
        throw new Error("Cannot delete built-in presets");
      }

      const updatedPresets = userPresets.filter((p) => p.id !== presetId);

      // Get existing caption settings or create default
      const existingSettings = project.captionSettings || {
        customColors: [],
        activeFontPresetId: "classic",
        customFontPresets: "[]",
        captionPosition: 85,
        fontSize: 24,
        textTransform: "none",
        captionAnimation: "none",
      };

      await saveCaptionSettings({
        projectId,
        captionSettings: {
          ...existingSettings,
          customFontPresets: JSON.stringify(updatedPresets),
        },
      });
    },
    [projectId, project, userPresets, saveCaptionSettings]
  );

  return {
    allPresets,
    userPresets,
    builtInPresets: BUILT_IN_PRESETS,
    savePreset,
    deletePreset,
    isLoading: project === undefined,
  };
}
